import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Users, ChevronUp, MessageCircle, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import IdeaCard from "@/components/ideas/IdeaCard";
import StageSection from "./StageSection";
import { FEATURED_IDEA_ID } from "@/lib/featured";

const GrowStage = () => {
  const { data: featured } = useQuery({
    queryKey: ["home-featured-idea", FEATURED_IDEA_ID],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ideas")
        .select("*")
        .eq("id", FEATURED_IDEA_ID)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const { data: others = [] } = useQuery({
    queryKey: ["home-grow-others"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ideas")
        .select("*")
        .eq("status", "published")
        .neq("id", FEATURED_IDEA_ID)
        .not("title", "ilike", "%emerging market%")
        .order("vote_count", { ascending: false })
        .limit(4);
      if (error) throw error;
      const ids = [...new Set((data || []).map((i: any) => i.user_id))];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("user_id, display_name, avatar_url")
        .in("user_id", ids);
      const map = new Map((profiles || []).map((p: any) => [p.user_id, p]));
      return (data || []).map((i: any) => ({ ...i, profiles: map.get(i.user_id) || null }));
    },
  });

  return (
    <StageSection
      id="grow"
      number="04"
      eyebrow="Grow · Community"
      title="Then rally the room around it."
      intro="Posting an idea here is how it picks up upvotes, comments, and follows from advocates, agencies, policymakers, and funders. Momentum gets it into the right meetings."
      icon={Users}
      ctaLabel="Post your idea"
      ctaHref="/ideas?new=1"
      tone="alt"
    >
      <div className="space-y-6">
        {featured && (
          <Link
            to={`/ideas/${featured.id}`}
            className="group block rounded-2xl border-2 border-primary/40 bg-card overflow-hidden hover:border-primary transition-all shadow-glow"
          >
            <div className="p-6 md:p-8">
              <div className="flex items-center gap-2 mb-3">
                <Badge className="bg-primary text-primary-foreground">
                  Featured idea
                </Badge>
                <Badge variant="outline">{featured.category}</Badge>
              </div>
              <h3 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-3 leading-tight">
                {featured.title}
              </h3>
              <p className="text-muted-foreground text-sm md:text-base mb-5 line-clamp-3 leading-relaxed">
                {featured.description}
              </p>
              <div className="flex items-center gap-5 text-sm text-foreground/80">
                <span className="inline-flex items-center gap-1.5">
                  <ChevronUp className="h-4 w-4 text-primary" />
                  <span className="font-semibold">{featured.vote_count ?? 0}</span>
                  <span className="text-muted-foreground">upvotes</span>
                </span>
                <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                  <MessageCircle className="h-4 w-4" />
                  Join the conversation
                </span>
                <span className="ml-auto inline-flex items-center gap-1.5 text-primary font-semibold group-hover:translate-x-0.5 transition-transform">
                  View idea <ArrowRight className="h-4 w-4" />
                </span>
              </div>
            </div>
          </Link>
        )}

        {others.length > 0 && (
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3 font-semibold">
              More ideas on the board
            </div>
            <div className="columns-1 sm:columns-2 gap-4">
              {others.map((idea: any) => (
                <IdeaCard
                  key={idea.id}
                  idea={idea}
                  hasVoted={false}
                  onVote={() => {}}
                  currentUserId={null}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </StageSection>
  );
};

export default GrowStage;